import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * 订单列表页的跨页勾选状态。
 *
 * <p><b>只存 key，不存顺序。</b>导出文件的行序不作保证（PRD 未要求，客户可在 Excel 里自行排序），
 * 服务端也不重排、不校验。实际表现是：勾选数不超过服务端的切片大小（500）时整份文件恒为 id 倒序，
 * 超过时呈分段递减。曾经为了让「提交顺序 = 列表展示顺序」而记录每行的全局展示序号，
 * 随行序保证一并去掉了——它的收益（>500 条且乱序翻页时才有别）和它自己的失效模式
 * （勾选期间有新单入库会让先前记下的序号偏小、进而排错）落在同一个狭窄区间，不划算。
 *
 * <p><b>为什么仍然是 Context 而不是页面内的 useState。</b>不是为了跨组件共享——消费者只有
 * OrderQueryPage 及其直接子组件，传 props 完全够用。理由是<b>生命周期</b>：
 * /order-query 与 /order-detail/:trackingNumber 是两个平级路由，点运单号进详情会把列表页卸载。
 * 状态放在页面里就死在这一跳上，客户勾了几十单、点开其中一单核对地址、回来发现勾全没了，
 * 而同一个页面的筛选条件与页码是特意用 sessionStorage 保住的（见 QUERY_STATE_KEY）——
 * 两者行为割裂。把状态提到 PortalLayout 那一层，它就能撑过这趟往返，且与恢复出来的筛选条件自洽。
 *
 * <p>状态只在内存里，<b>不落 localStorage</b>：勾选是一次导出操作的中间态，跨浏览器会话恢复它
 * 只会让客户面对一份来历不明的选中列表。
 */

interface OrderSelectionContextValue {
    selectedKeys: string[];
    setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

/*
 * 默认值取 null 并在 useOrderSelection 里抛错，不给一个「空数组 + 空操作」的良性默认。
 *
 * 良性默认下漏挂 Provider 的表现是：checkbox 点得动、但一个也勾不上，
 * 没有任何报错——正是这个功能最不该有的那类静默失败。宁可在首次渲染就炸。
 */
const OrderSelectionContext = createContext<OrderSelectionContextValue | null>(null);

/**
 * 勾选态的宿主。
 *
 * <b>必须挂在 /order-query 与 /order-detail 之上</b>（当前挂在 PortalLayout 那一层），
 * 挂进列表页内部就退化成 useState，白丢详情页往返这条通路。
 */
export const OrderSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const value = useMemo(() => ({ selectedKeys, setSelectedKeys }), [selectedKeys]);
    return <OrderSelectionContext.Provider value={value}>{children}</OrderSelectionContext.Provider>;
};

export interface OrderSelection {
    /** 传给 antd rowSelection.selectedRowKeys，同时也是提交给服务端的那份运单号 */
    selectedKeys: string[];
    selectedCount: number;
    /** 传给 antd rowSelection.onChange */
    onChange: (keys: React.Key[]) => void;
    clear: () => void;
}

/** 读取跨页勾选态。行数据与页偏移都不再需要——只记 key，不记位置。 */
export const useOrderSelection = (): OrderSelection => {
    const context = useContext(OrderSelectionContext);
    if (!context) {
        throw new Error('useOrderSelection 必须在 OrderSelectionProvider 之内使用');
    }
    const { selectedKeys, setSelectedKeys } = context;

    /*
     * antd 在开了 preserveSelectedRowKeys 时，回调里给的就是「含不在本页的」完整选中集合，
     * 直接整体替换即可，不需要自己做增删合并。
     */
    const onChange = useCallback(
        (keys: React.Key[]) => setSelectedKeys(keys.map(String)),
        [setSelectedKeys]
    );

    const clear = useCallback(() => setSelectedKeys([]), [setSelectedKeys]);

    return { selectedKeys, selectedCount: selectedKeys.length, onChange, clear };
};
